import React, { useState, useContext, useEffect } from "react";
import { NavLink, useHistory } from "react-router-dom";
import {
  makeStyles,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ButtonBase,
  Divider,
} from "@material-ui/core";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";

import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import BackdropLoading from "../components/BackdropLoading";
import { Can } from "../components/Can";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import logoLight from "../assets/logo-black.png";
import logoDark from "../assets/logo-white.png";

const OFFLINE_STATUSES = ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"];

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "100dvh",
    },
  },
  appBar: {
    backgroundColor: theme.palette.cards.background,
    color: theme.palette.text.primary,
    borderBottom: theme.border.divider,
    boxShadow: "none",
  },
  toolbar: {
    minHeight: theme.sizing.appBar,
    gap: theme.spacing(2),
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 2),
      gap: theme.spacing(1),
    },
  },
  logo: {
    height: 34,
    flex: "none",
  },
  nav: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    gap: theme.spacing(0.5),
    overflowX: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  navLink: {
    position: "relative",
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.borderRadius.small,
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontWeight: 500,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  },
  navLinkActive: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.tintedBackground,
    fontWeight: 600,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.tintedBackground,
    },
  },
  connectionDot: {
    width: 8,
    height: 8,
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.palette.error.main,
  },
  actions: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  userButton: {
    borderRadius: theme.borderRadius.pill,
    padding: 2,
  },
  userAvatar: {
    width: 34,
    height: 34,
    fontSize: "0.875rem",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  userHeader: {
    padding: theme.spacing(1, 2),
    display: "flex",
    flexDirection: "column",
  },
  userProfile: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "capitalize",
  },
  appBarSpacer: {
    minHeight: theme.sizing.appBar,
  },
  content: {
    flex: 1,
    minWidth: 0,
    overflow: "auto",
  },
}));

const TopNavLink = ({ to, label, exact, isActive, adornment }) => {
  const classes = useStyles();

  return (
    <NavLink
      to={to}
      exact={exact}
      isActive={isActive}
      className={classes.navLink}
      activeClassName={classes.navLinkActive}
    >
      {label}
      {adornment}
    </NavLink>
  );
};

const UserMenu = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, handleLogout } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);

  const handleNavigate = (path) => {
    setAnchorEl(null);
    history.push(path);
  };

  return (
    <>
      <ButtonBase
        aria-label={i18n.t("mainDrawer.appBar.user.profile")}
        aria-haspopup="true"
        aria-controls="menu-user"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={classes.userButton}
      >
        <Avatar className={classes.userAvatar}>{user.name?.charAt(0)}</Avatar>
      </ButtonBase>
      <Menu
        id="menu-user"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <div className={classes.userHeader}>
          <strong>{user.name}</strong>
          <span className={classes.userProfile}>{user.profile}</span>
        </div>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setUserModalOpen(true);
          }}
        >
          {i18n.t("mainDrawer.appBar.user.profile")}
        </MenuItem>
        <Can
          role={user.profile}
          perform="drawer-admin-items:view"
          yes={() => [
            <MenuItem key="users" onClick={() => handleNavigate("/users")}>
              {i18n.t("mainDrawer.listItems.users")}
            </MenuItem>,
            <MenuItem key="settings" onClick={() => handleNavigate("/settings")}>
              {i18n.t("mainDrawer.listItems.settings")}
            </MenuItem>,
          ]}
        />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleLogout();
          }}
        >
          {i18n.t("mainDrawer.appBar.user.logout")}
        </MenuItem>
      </Menu>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
    </>
  );
};

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const { loading, user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setConnectionWarning(
        whatsApps.some((whats) => OFFLINE_STATUSES.includes(whats.status))
      );
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <AppBar position="absolute" className={classes.appBar}>
        <Toolbar variant="dense" className={classes.toolbar}>
          <img
            src={darkMode ? logoDark : logoLight}
            alt="Simetrik Chat"
            className={classes.logo}
          />
          <nav className={classes.nav} aria-label={i18n.t("topNav.label")}>
            <TopNavLink
              to="/tickets"
              label={i18n.t("topNav.chats")}
              isActive={(match, location) =>
                location.pathname === "/" ||
                location.pathname.startsWith("/tickets")
              }
            />
            <TopNavLink to="/summary" label={i18n.t("topNav.summary")} />
            <TopNavLink
              to="/contacts"
              label={i18n.t("mainDrawer.listItems.contacts")}
            />
            <TopNavLink
              to="/connections"
              label={i18n.t("mainDrawer.listItems.connections")}
              adornment={
                connectionWarning && (
                  <span
                    className={classes.connectionDot}
                    aria-label={i18n.t("topNav.connectionWarning")}
                  />
                )
              }
            />
            <Can
              role={user.profile}
              perform="drawer-admin-items:view"
              yes={() => (
                <TopNavLink
                  to="/reports"
                  label={i18n.t("mainDrawer.listItems.reports")}
                />
              )}
            />
          </nav>
          <div className={classes.actions}>
            <IconButton
              aria-label={i18n.t("mainDrawer.appBar.darkMode")}
              onClick={toggleTheme}
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            {user.id && <NotificationsPopOver />}
            <UserMenu />
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
